<template>
  <!--コンテンツ-->
  <main class="renew3_top">
    <div class="renew3_wrapper">
      <h1 class="top_h1">
        履歴書・職務経歴書を無料で作成｜スマホ・パソコン対応
      </h1>
    </div>

    <!-- static:skip -->
    <!--紹介URL(/r/:code)経由で来た人にだけ出る訴求。localStorage を見て
        出し分けるので、全ユーザー共通の静的トップには焼かない。
        紹介URL以外からの来訪なので「対象外です」は出さない -->
    <div class="renew3_wrapper">
      <referral-invitee-banner :show-ineligible="false" />
    </div>
    <!-- /static:skip -->

    <!--メインバナー（週替わり。掲載表は utils/top/topBannerSchedule.json）-->
    <!-- static:skip -->
    <div class="mv">
      <div class="renew3_wrapper mv_wrapper">
        <a
          :href="topBanner.href"
          target="_blank"
          rel="noopener noreferrer"
          class="mv_link"
        >
          <img
            :src="topBanner.src"
            :alt="topBanner.alt"
            :class="['mv_img', { mv_img_rounded: topBanner.isScheduled }]"
          />
        </a>
      </div>
    </div>
    <!-- /static:skip -->
    <!-- static:top-banner -->

    <!--バナー下の3カラム帯（実績・スピード・シェア）。3つ目はシェア導線の入口 -->
    <top-feature-strip />

    <!-- static:skip -->
    <top-notice-bar />
    <!-- /static:skip -->

    <!--テンプレート選択-->
    <top-format-list />

    <!--ヤギッシュのサービス-->
    <top-service-steps />

    <!--履歴書完成イメージ-->
    <top-resume-preview />

    <!--お役立ちコラム-->
    <!-- static:skip -->
    <top-knowhow-list />
    <!-- /static:skip -->

    <!--お問い合わせ-->
    <top-contact />

    <!--バナー-->

    <!--<floating-button />-->
  </main>
</template>
<style lang="scss" scoped>
@import '@/assets/scss/variables';
@import '@/assets/scss/top/mv-banner';

/* 共通 */
.renew3_top :deep(.renew3_wrapper) {
  width: 100%;
  max-width: 980px;
  margin: 0 auto;
  padding: 0 20px;
}

/* ページタイトル */
.renew3_top .top_h1 {
  font-weight: bold;
  font-size: 18px;
  line-height: 1.5;
  margin-bottom: 12px;
}

.renew3_top :deep(.format_more_btn) {
  display: inline-block;
  color: #e95b10;
  font-size: 14px;
  font-weight: bold;
  text-decoration: none;
  transition: transform 0.2s ease;
}

.renew3_top :deep(.format_more_btn):hover {
  transform: scale(1.05);
  opacity: 1;
  text-decoration: none;
  color: #e95b10;
}

/* SP */
@media screen and (width <= 640px) {
  /* 共通 */
  .renew3_top :deep(.renew3_wrapper) {
    width: 100%;
  }

  /* ページタイトル */
  .renew3_top .top_h1 {
    font-size: 15px;
    margin-bottom: 10px;
  }
}
</style>
<script setup lang="ts">
/**
 * トップページ。中身はすべて子コンポーネントで、この SFC が持つのは
 * 流入パラメータ（アフィリエイト / OEM）の Cookie 保存だけ。
 *
 * ⚠️ 静的トップ(public/top-content.html)はこの <template> から
 * scripts/extract-top-static.js が生成する。API 取得コンテンツは
 * <!-- static:skip --> で囲んで焼かれないようにすること。
 */
import { computed, getCurrentInstance, onBeforeMount } from 'vue'
import { useNow } from '@vueuse/core'
import { resolveTopBanner } from '@/utils/top/topBannerSchedule'
import TopContact from '@/components/top/TopContact.vue'
import TopFeatureStrip from '@/components/top/TopFeatureStrip.vue'
import TopFormatList from '@/components/top/TopFormatList.vue'
import TopKnowhowList from '@/components/top/TopKnowhowList.vue'
import TopNoticeBar from '@/components/top/TopNoticeBar.vue'
import TopResumePreview from '@/components/top/TopResumePreview.vue'
import TopServiceSteps from '@/components/top/TopServiceSteps.vue'
import ReferralInviteeBanner from '@/components/yagiprime/ReferralInviteeBanner.vue'

// MV バナーの週替わり。開きっぱなしのタブでも日付跨ぎで差し替わるよう毎時更新
const now = useNow({ interval: 60 * 60 * 1000 })
const topBanner = computed(() => resolveTopBanner(now.value))

/** Cookie を配る対象ドメイン。localhost は開発用 */
const COOKIE_DOMAINS = ['yagish.jp', 'localhost']
const COOKIE_MAX_AGE_SEC = 60 * 60 * 24
/** OEM 経由の流入として受け付ける ID。ここに無い oemId は無視する */
const ALLOWED_OEM_IDS = ['kokudo-bl']

const props = defineProps({
  /** /aff/:affId 経由の流入。アフィリエイト ID として Cookie に残す */
  affId: {
    type: String,
    default: ''
  },
  /** /oem/:oemId 経由の流入 */
  oemId: {
    type: String,
    default: ''
  }
})

type CookiesApi = {
  config: (expireTimes: number, path: string) => void
  set: (
    key: string,
    value: string,
    expire: null,
    path: null,
    domain: string
  ) => void
}
type GlobalVar = { googleAdsTagCheck: (a: string, b: string) => void }

const vm = getCurrentInstance()!.proxy as unknown as {
  $cookies: CookiesApi
  $globalVar: GlobalVar
}

function setCookieOnAllDomains(key: string, value: string): void {
  vm.$cookies.config(COOKIE_MAX_AGE_SEC, '')
  for (const domain of COOKIE_DOMAINS) {
    vm.$cookies.set(key, value, null, null, domain)
  }
}

onBeforeMount(() => {
  vm.$globalVar.googleAdsTagCheck('', '')

  if (props.affId) {
    setCookieOnAllDomains('_YAGISH_AFFILIATE_ID', props.affId)
  }
  // OEM は OEM ID とアフィリエイト ID の両方に入れる（従来の挙動を維持）
  if (ALLOWED_OEM_IDS.indexOf(props.oemId) >= 0) {
    setCookieOnAllDomains('_YAGISH_OEM_ID', props.oemId)
    setCookieOnAllDomains('_YAGISH_AFFILIATE_ID', props.oemId)
  }
})
</script>
